import { Service } from '../classes';
import { register } from './data-provider-register';
import { A9DataProviderHelper } from './data-provider-helper';
import { A9HttpDataService, MAX_PAGE_SIZE } from './http-data-service';
import {
    Events,
    ActionTypes,
    type IAction,
    type ICollectionParams,
    type IItem,
    type IList,
    type IPartialItem,
    type IResourceParams,
    type IDataProviderHelper,
    type IDataProvider,
    type IHttpDataService,
    type HttpUpdateMethod,
    type HttpCreateMethod,
    type HttpDeleteMethod
} from './models';
import { ItemResource, CollectionResource } from './resource';
import { ApiError } from './error';
import { fetchAllWithPagination } from './fetch-all-with-pagination';

export { type IDataProvider };

export class SimpleDataProvider extends Service {
    protected helper: IDataProviderHelper;
    protected service: IHttpDataService;

    private static _instance: SimpleDataProvider;

    constructor(..._args: any) {
        super();
        this.initDataService();
        this.initDataProviderHelper();
        register.subscribe(this);
    }

    protected initDataService() {
        this.service = new A9HttpDataService();
    }

    protected initDataProviderHelper() {
        this.helper = new A9DataProviderHelper();
    }

    public isServicePath(_path: string) {
        return false;
    }

    /**
     * Handler of data changes
     */
    public fire(hash: string, action: IAction) {
        //console.log("FIRE", this.path, hash, action);
        this.emit(Events.Updated, { forResource: hash, action: action });
    }

    public isError(error: ApiError, code: string | string[]): boolean {
        return this.service.isError(error, code);
    }

    public getErrorMessage(error: ApiError, code: string): string {
        return this.service.getErrorMessage(error, code);
    }

    protected notify(action: IAction, strict: boolean = false) {
        register.notify(action, strict);
    }

    public static getInstance<T extends typeof SimpleDataProvider>(this: T, ...args: any): InstanceType<T> {
        return (this._instance || (this._instance = new this(...args))) as InstanceType<T>;
    }
}

export abstract class BaseDataProvider extends SimpleDataProvider {
    protected url: string;
    protected path: string;
    protected loadPromises: any;

    protected updateMethod: HttpUpdateMethod = 'PUT';
    protected patchMethod: HttpUpdateMethod = 'PATCH';
    protected addMethod: HttpCreateMethod = 'POST';
    protected removeMethod: HttpDeleteMethod = 'DELETE';

    protected constructor() {
        super();
        this.url = '';
        this.path = '';
        this.loadPromises = [];
    }

    public setApiUrl(url: string) {
        this.url = url;
        if (this.url[this.url.length - 1] === '/') {
            this.url = this.url.slice(0, -1);
        }
        this.path = this.helper.urlToPath(this.url);
    }

    public get apiUrl(): string {
        return this.url;
    }

    public getResourceUrl(resourceParams: IResourceParams = {}): string {
        return this.helper.buildUrl(this.url, resourceParams);
    }

    public abstract getCollectionResource(resourceParams: IResourceParams, debug?: boolean);

    public abstract getItemResource(resourceParams: IResourceParams);

    protected getTList<T>(collectionParams: ICollectionParams, resourceParams: IResourceParams, cancel: Promise<void>): Promise<IList<T>> {
        return this.service.getList<T>(this.getResourceUrl(resourceParams), this.buildListingRequestParams(collectionParams), cancel).then(list => {
            if (list && list.items) {
                list.items = this.handleList(list.items);
            }
            return list || null;
        });
    }

    protected getTItem<T>(resourceParams: IResourceParams, params: object, cancel: Promise<void>): Promise<T> {
        return this.service.getItem<T>(this.getResourceUrl(resourceParams), params, cancel).then(item => {
            return this.handleItem(item);
        });
    }

    protected updateTItem<I, O>(
        item: I,
        resourceParams: IResourceParams,
        cancel: Promise<void>,
        stealth: boolean = false,
        doPackItem: boolean = true
    ): Promise<O> {
        const url = this.getResourceUrl(resourceParams);
        return this.service
            .updateRequest<O>(url, this.updateMethod, (doPackItem && this.packItem(item)) || item, {}, cancel)
            .then((result: angular.IHttpResponse<O> | O) => {
                if (!stealth) {
                    this.notify({ type: ActionTypes.Update, hash: url });
                }
                const data = result['data'] || result;
                return this.handleItem(data);
            });
    }

    protected patchTItem<I, O>(partialItem: I, resourceParams: IResourceParams, cancel: Promise<void>, stealth: boolean = false): Promise<O> {
        return this.getTItem<O>(resourceParams, {}, cancel).then(item => {
            const data = partialItem as any;
            for (const key in item)
                if (Object.prototype.hasOwnProperty.call(item, key) && Object.prototype.hasOwnProperty.call(data, key) && data[key] !== undefined) {
                    item[key] = data[key];
                }
            return this.updateTItem<O, O>(item, resourceParams, cancel, stealth, false);
        });
    }

    protected originalPatchTItem<Item, Response>(
        partialItem: Item,
        resourceParams: IResourceParams,
        cancel: Promise<void>,
        stealth: boolean = false
    ): Promise<Response> {
        const url = this.getResourceUrl(resourceParams);

        return this.service
            .updateRequest<Response, Item, IResourceParams>(url, this.patchMethod, partialItem, {}, cancel)
            .then((result: angular.IHttpResponse<Response> | Response) => {
                if (!stealth) {
                    this.notify({ type: ActionTypes.Update, hash: url });
                }
                const data = result['data'] || result;
                return this.handleItem(data);
            });
    }

    protected addTItem<Item, Response>(
        item: Item,
        resourceParams: IResourceParams,
        cancel: Promise<void>,
        stealth: boolean = false,
        doPackItem: boolean = true
    ): Promise<Response> {
        const url = this.getResourceUrl(resourceParams);
        return this.service
            .updateRequest<Response>(url, this.addMethod, (doPackItem && this.packItem(item)) || item, {}, cancel)
            .then((result: angular.IHttpResponse<Response> | Response) => {
                if (!stealth) {
                    this.notify({ type: ActionTypes.Add, hash: url });
                }
                const data = result['data'] || result;
                return this.handleItem(data);
            });
    }

    protected removeTItem(resourceParams: IResourceParams, cancel: Promise<void>, stealth: boolean = false): Promise<any> {
        const url = this.getResourceUrl(resourceParams);
        return this.service.updateRequest<any>(url, this.removeMethod, {}, {}, cancel).then((result: angular.IHttpResponse<any>) => {
            if (!stealth) {
                this.notify({ type: ActionTypes.Remove, hash: url });
            }
            return result;
        });
    }

    public isServicePath(path: string) {
        return path === this.path;
    }

    /**
     * Handler of data changes
     */

    protected abstract handleList(result: any[]);

    protected abstract handleListItem(result: any);

    protected abstract handleItem(result: any);

    protected abstract packItem(item: any);

    protected buildListingRequestParams(params: ICollectionParams) {
        let requestParams = null;
        try {
            requestParams = this.helper.createRequestParams(params);
        } catch (ex) {
            ex.push('url:');
            ex.push(this.url);
            console.error.apply(console, ex);
        }

        return requestParams;
    }

    public getPageNames(): Promise<any> {
        return Promise.resolve({});
    }
}

/**
 * @deprecated
 * @use AbstractDataProviderV2
 */
export class AbstractDataProvider<T> extends BaseDataProvider implements IDataProvider<T> {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    constructor(...args: any[]) {
        super();
    }

    public getCollectionResource(resourceParams: IResourceParams = {}, debug = false): CollectionResource<T> {
        return new CollectionResource<T>(this, resourceParams, this.getResourceUrl(resourceParams), debug);
    }

    public getItemResource(resourceParams: IResourceParams = {}): ItemResource<T> {
        return new ItemResource<T>(this, resourceParams, this.getResourceUrl(resourceParams));
    }

    /**
     * Fetches all paginated results and returns a combined list.
     *
     * - Applies page size limits
     * - Iterates through all pages via `fetchAllWithPagination`
     * - Preserves metadata from the last response
     *
     * @param collectionParams - Query + pagination parameters
     * @param resourceParams - Additional request parameters
     * @param timeout - Optional request timeout
     * @private
     */
    private async fetchAllList(
        collectionParams: ICollectionParams = {},
        resourceParams: IResourceParams = {},
        timeout: Promise<void> = null
    ): Promise<IList<T>> {
        // Clone pagination so the caller's (shared) collection params are not mutated
        const params: ICollectionParams = {
            ...collectionParams,
            pagination: {
                number: 0,
                size: Math.min(collectionParams.pagination?.size ?? MAX_PAGE_SIZE, MAX_PAGE_SIZE)
            }
        };

        let lastPage: IList<T> = null;

        const fetchPage = async (page: ICollectionParams) => {
            const response = await this.getTList<T>(page, resourceParams, timeout);
            lastPage = response;
            return response;
        };

        const items = await fetchAllWithPagination<T, ICollectionParams>(fetchPage, params);

        return {
            ...lastPage,
            items
        };
    }

    public list(collectionParams: ICollectionParams = {}, resourceParams: IResourceParams = {}, timeout: Promise<void> = null): Promise<IList<T>> {
        /**
         * AbstractDataProvider does not support an `all` method.
         * If the requested page size exceeds the API limit, fallback to fetching all
         * data through multiple paginated requests for backwards compatibility.
         */
        if (collectionParams?.pagination?.size > MAX_PAGE_SIZE) {
            return this.fetchAllList(collectionParams, resourceParams, timeout);
        }
        return this.getTList<T>(collectionParams, resourceParams, timeout);
    }

    public subscribeItem($scope: angular.IScope, resourceParams: IResourceParams = {}, fnDataCallback: Function, fnErrorCallback: Function) {
        let resource: ItemResource<IResourceParams> | null = this.getItemResource(resourceParams);
        resource.onLoaded(item => {
            fnDataCallback(item);
        });
        resource.onUpdated(() => {
            if (resource) {
                resource.reload(true);
            }
        });
        resource.onFail(error => {
            fnErrorCallback(error);
        });
        const undestroy = $scope.$on('$destroy', () => {
            undestroy();
            if (resource) {
                resource.close();
                resource = null;
            }
        });
        return resource.reload(true);
    }

    public get(resourceParams: IResourceParams = {}, params: object = {}, cancel: Promise<void> = null): Promise<T> {
        return this.getTItem<T>(resourceParams, params, cancel);
    }

    public update(
        item: IPartialItem,
        resourceParams: IResourceParams = {},
        cancel: Promise<void> = null,
        stealth: boolean = false,
        doPackItem: boolean = true
    ): Promise<T> {
        return this.updateTItem<IPartialItem, T>(item, resourceParams, cancel, stealth, doPackItem);
    }

    public patch(
        partialItem: IPartialItem,
        resourceParams: IResourceParams = {},
        cancel: Promise<void> = null,
        stealth: boolean = false
    ): Promise<T> {
        return this.patchTItem<IPartialItem, T>(partialItem, resourceParams, cancel, stealth);
    }

    public add(
        item: IPartialItem,
        resourceParams: IResourceParams = {},
        cancel: Promise<void> = null,
        stealth: boolean = false,
        doPackItem: boolean = true
    ): Promise<T> {
        return this.addTItem<IPartialItem, T>(item, resourceParams, cancel, stealth, doPackItem);
    }

    public remove(resourceParams: IResourceParams = {}, cancel: Promise<void> = null, stealth: boolean = false): Promise<any> {
        return this.removeTItem(resourceParams, cancel, stealth);
    }

    /**
     * List request result handler
     */
    protected handleList(result: any[]): T[] {
        return result.map(item => this.handleListItem(item));
    }

    /**
     * Item request result handler
     */
    protected handleItem(result: any): T {
        return result as T;
    }

    protected handleListItem(result: any): T {
        return result as T;
    }

    /**
     * Pack item for send to backend
     */
    protected packItem(item: any): T {
        return item as T;
    }
}

export class DataProvider extends AbstractDataProvider<IItem> {}
